import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardMedia,
  IconButton,
  Chip,
  InputAdornment,
} from "@mui/material";
import { Close as CloseIcon, CloudUpload as UploadIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { toast } from "react-hot-toast";
import apiService from "../../services/api";
import { CreativeLookTemplate, CreativeLookTemplateFormData } from "../../types";
import { getImageUrl } from "../../utils/imageUrlHelper";

interface CreativeLookFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  template?: CreativeLookTemplate | null;
}

const CreativeLookForm = ({ open, onClose, onSuccess, template }: CreativeLookFormProps) => {
  const [formData, setFormData] = useState<CreativeLookTemplateFormData>({
    name: "",
    description: "",
    promptText: "",
    category: "casual",
    tags: [],
    credits: 1,
    displayOrder: 0,
    status: "active",
  });

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [tagInput, setTagInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const categories = [
    { value: "formal", label: "Formal" },
    { value: "casual", label: "Casual" },
    { value: "traditional", label: "Traditional" },
    { value: "fantasy", label: "Fantasy" },
    { value: "professional", label: "Professional" },
    { value: "seasonal", label: "Seasonal" },
    { value: "artistic", label: "Artistic" },
    { value: "historical", label: "Historical" },
    { value: "futuristic", label: "Futuristic" },
    { value: "other", label: "Other" }
  ];

  const statuses = [
    { value: "draft", label: "Draft" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name,
        description: template.description || "",
        promptText: template.promptText,
        category: template.category,
        tags: template.tags || [],
        credits: template.credits,
        displayOrder: template.displayOrder,
        status: template.status,
      });

      if (template.referenceImageUrl) {
        setImagePreview(getImageUrl(template.referenceImageUrl));
      }
    } else {
      // Reset form for new template
      setFormData({
        name: "",
        description: "",
        promptText: "",
        category: "casual",
        tags: [],
        credits: 1,
        displayOrder: 0,
        status: "draft",
      });
      setImageFile(null);
      setImagePreview(null);
      setTagInput("");
    }
    setError("");
  }, [template, open]);

  const handleInputChange = (field: keyof CreativeLookTemplateFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError("");
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!["image/jpeg", "image/png", "image/webp", "image/jpg"].includes(file.type)) {
        setError("Please select a valid image (JPEG, PNG, or WebP)");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError("Image must be less than 5MB");
        return;
      }

      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setError("");
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      handleInputChange("tags", [...formData.tags, tagInput.trim()]);
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    handleInputChange(
      "tags",
      formData.tags.filter((tag) => tag !== tagToRemove)
    );
  };

  const handleTagInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError("Name is required");
      return false;
    }

    if (!formData.promptText.trim()) {
      setError("Prompt text is required");
      return false;
    }

    if (formData.credits < 1) {
      setError("Credits must be at least 1");
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError("");

      let response;
      if (template) {
        // Update existing template
        response = await apiService.updateCreativeLookTemplate(template._id, formData);
      } else {
        // Create new template
        response = await apiService.createCreativeLookTemplate(formData);
      }

      if (response.success && response.data) {
        // Upload image if selected
        if (imageFile) {
          try {
            await apiService.uploadCreativeLookImage(response.data._id, imageFile);
          } catch (imgErr) {
            console.error("Image upload failed:", imgErr);
            toast.error("Template saved, but image upload failed");
          }
        }

        toast.success(template ? "Template updated successfully" : "Template created successfully");
        onSuccess();
      } else {
        setError(response.error || "Failed to save template");
      }
    } catch (err: any) {
      console.error("Error saving template:", err);
      setError(err.response?.data?.error || err.message || "Failed to save template");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{template ? "Edit Template" : "Add New Template"}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Name */}
          <TextField label="Name *" fullWidth value={formData.name} onChange={(e) => handleInputChange("name", e.target.value)} disabled={loading} />

          {/* Description */}
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={2}
            value={formData.description}
            onChange={(e) => handleInputChange("description", e.target.value)}
            disabled={loading}
            helperText="User-visible description of this template"
          />

          {/* Prompt Text */}
          <TextField
            label="AI Prompt Text *"
            fullWidth
            multiline
            rows={4}
            value={formData.promptText}
            onChange={(e) => handleInputChange("promptText", e.target.value)}
            disabled={loading}
            helperText="Hidden AI prompt used for generation (not visible to users)"
          />

          {/* Category and Status */}
          <Box sx={{ display: "flex", gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Category *</InputLabel>
              <Select value={formData.category} onChange={(e) => handleInputChange("category", e.target.value)} label="Category *" disabled={loading}>
                {categories.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={formData.status} onChange={(e) => handleInputChange("status", e.target.value)} label="Status" disabled={loading}>
                {statuses.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Credits and Display Order */}
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Credits *"
              type="number"
              fullWidth
              value={formData.credits}
              onChange={(e) => handleInputChange("credits", parseInt(e.target.value) || 1)}
              disabled={loading}
              inputProps={{ min: 1, max: 100 }}
              helperText="Credit cost per generation"
            />

            <TextField
              label="Display Order"
              type="number"
              fullWidth
              value={formData.displayOrder}
              onChange={(e) => handleInputChange("displayOrder", parseInt(e.target.value) || 0)}
              disabled={loading}
              helperText="Lower numbers appear first"
            />
          </Box>

          {/* Tags */}
          <Box>
            <TextField
              label="Tags"
              fullWidth
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={handleTagInputKeyPress}
              disabled={loading}
              helperText="Press Enter to add tags"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button onClick={handleAddTag} disabled={!tagInput.trim() || loading}>
                      Add
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
              {formData.tags.map((tag, index) => (
                <Chip key={index} label={tag} onDelete={() => handleRemoveTag(tag)} size="small" disabled={loading} />
              ))}
            </Box>
          </Box>

          {/* Image Upload */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Reference Image
            </Typography>
            <input type="file" ref={fileInputRef} onChange={handleImageChange} accept="image/jpeg,image/jpg,image/png,image/webp" style={{ display: "none" }} />

            {imagePreview ? (
              <Card sx={{ maxWidth: 300, position: "relative" }}>
                <CardMedia component="img" height="200" image={imagePreview} alt="Preview" />
                <IconButton
                  onClick={handleRemoveImage}
                  disabled={loading}
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    bgcolor: "background.paper",
                    "&:hover": { bgcolor: "error.light" },
                  }}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Card>
            ) : (
              <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => fileInputRef.current?.click()} disabled={loading} sx={{ width: "fit-content" }}>
                Upload Image
              </Button>
            )}
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
              Max size: 5MB. Supported: JPEG, PNG, WebP
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading} startIcon={loading && <CircularProgress size={20} />}>
          {loading ? "Saving..." : template ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreativeLookForm;
